import { DateTime } from "luxon"
import { useEffect, useMemo, useRef, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"

import { useHydrate } from "@/store"

export const usePanelLink = () => {
  const { slug } = useParams()
  return (path: string) => `/panel/${slug}/${path}`
}

export const usePanelLinkWithPageParams = () => {
  const linkTo = usePanelLink()
  const [searchParams, _setSearchParams] = useSearchParams()

  return (link: string, overrides = {}) => {
    const query = searchParams.get("query")
    const perPage = searchParams.get("per_page")
    const newPage = searchParams.get("page")

    // filter nulls and undefined
    const params = Object.fromEntries(
      Object.entries({ query, perPage, page: newPage, ...overrides }).filter(([, value]) => value != null),
    )

    return `${linkTo(link)}?${new URLSearchParams(params)}`
  }
}

type Cursor = {
  first?: number
  last?: number
  after?: string
  before?: string
}

type PageInfo = {
  startCursor?: string | null
  endCursor?: string | null
  hasPreviousPage?: boolean
  hasNextPage?: boolean
}

export const useCursorPagination = ({ perPage = 25 }: { perPage: number }) => {
  const [cursor, setCursor] = useState<Cursor>({ first: perPage })

  const useNavigationFunctions = (pageInfo?: PageInfo) => {
    return useMemo(() => {
      if (!pageInfo) {
        return { nextPage: () => {}, previousPage: () => {}, hasPreviousPage: false, hasNextPage: false }
      }

      const nextPage = () => setCursor({ first: perPage, after: pageInfo?.endCursor })
      const previousPage = () => setCursor({ last: perPage, before: pageInfo?.startCursor })

      const { hasPreviousPage, hasNextPage } = pageInfo

      return { nextPage, previousPage, hasPreviousPage, hasNextPage }
    }, [pageInfo, perPage])
  }

  return { cursor, createNavigationFunctions: useNavigationFunctions }
}

export const useReservationTabHydrate = (venue) =>
  useHydrate({
    selectedTableGroupIds: venue.tableGroups.map((tg) => tg.id),
    currentDate: DateTime.local({ zone: venue.timezone }),
  })

export const useDelayedRendering = (delay = 100) => {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (ready) {
      setReady(false)
    }

    const timeout = setTimeout(() => {
      setReady(true)
    }, delay)

    return () => {
      clearTimeout(timeout)
    }
  }, [delay])

  return ready
}

// Returns an array with the value of the flag and a method that accepts a delay as argument.
// When the method is called, the flag is flipped to `true` until the delay after which it's set back to `false`.
// If the method is called multiple times, the previous delay is canceled and the new one is set.
// This hook is useful for temporary states, toast messages, etc.
export const useFlagWithDelay = () => {
  const [flag, setFlag] = useState(false)
  const timeout = useRef(null)

  const setFlagWithDelay = (delay = 100) => {
    setFlag(true)

    if (timeout.current) {
      clearTimeout(timeout.current)
    }

    timeout.current = setTimeout(() => setFlag(false), delay)
  }

  return [flag, setFlagWithDelay] as const
}

export const usePreviousUntilDefined = (value?: any) => {
  const [state, setState] = useState(value)

  useEffect(() => {
    if (value !== undefined) {
      setState(value)
    }
  }, [value])

  return state
}
